import { useEffect, useState, useRef } from 'react';
import Boundary from '@app/Boundary';
import RoutesRoot from '@routes/index';
import useThemeStore from '@stores/theme.store';
import { ThemeProvider } from 'styled-components';
import { getTheme, getAntdTheme } from '@theme/theme';
import { AppScrollbarsGlobalStyle } from '@theme/appScrollbarsGlobalStyle';
import MessageProvider from '@context/MessageProvider';
import AbilityProvider from '@/ability/AbilityProvider';
import ViteHMRHandler from '@/app/configs/ViteHMRHandler';
import NotificationProvider from '@context/NotificationProvider';
import PreloadErrorOverlay from '@/app/configs/PreloadErrorOverlay';
import AppDeployUpdateBar from '@/components/ui/AppDeployUpdateBar';
import NotificationBannerHost from '@/components/notifications/NotificationBannerHost';
import { ConfigProvider, App as AntApp, theme as antdTheme } from 'antd';
import Cube3DLoader from '@/components/ui/Cube3DLoader';

import { QueryClientProvider, focusManager } from '@tanstack/react-query';
import { useAuthStore } from '@stores/auth.store';
import {
    appQueryClient,
    pauseAppQueriesForRecovery,
    resetAppQueryClientCaches,
    resumeAppQueriesAfterRecovery,
} from '@app/appQueryClient';
import {
    markTabHidden,
    markTabVisible,
    runSessionPoolRecovery,
    shouldRunWakeRecovery,
} from '@utils/sessionNetworkCoordinator';

const App = () => {
    const mode = useThemeStore((state) => state.mode);
    const currentTheme = getTheme(mode);
    const antdThemeConfig = getAntdTheme(mode);

    // Apply dark algorithm for Ant Design
    const algorithm = mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm;

    const processComponents = (components) => {
        const processed = {};
        Object.keys(components).forEach((componentName) => {
            const config = components[componentName];
            processed[componentName] = {
                ...config,
                fontFamily: config.fontFamily || currentTheme.fonts.family,
            };
        });
        return processed;
    };

    const finalAntdConfig = {
        ...antdThemeConfig,
        algorithm,
        components: {
            ...processComponents(antdThemeConfig.components),
        },
    };

    const [authRehydrated, setAuthRehydrated] = useState(false);
    const wasAuthenticatedRef = useRef(null);

    useEffect(() => {
        let done = false;
        const markRehydrated = () => {
            if (!done) {
                done = true;
                setAuthRehydrated(true);
            }
        };
        useAuthStore.persist?.onFinishHydration?.(markRehydrated);
        const t = setTimeout(markRehydrated, 150);
        return () => clearTimeout(t);
    }, []);

    // Pause queries while hidden; on return from idle run ordered pool recovery.
    useEffect(() => {
        focusManager.setEventListener((handleFocus) => {
            if (typeof window === 'undefined' || !window.addEventListener) return undefined;

            const onVisibility = () => {
                if (document.visibilityState === 'hidden') {
                    markTabHidden();
                    handleFocus(false);
                    return;
                }
                const { hiddenMs } = markTabVisible();
                if (shouldRunWakeRecovery(hiddenMs)) {
                    pauseAppQueriesForRecovery();
                    void Promise.resolve(runSessionPoolRecovery({ reason: 'wake' })).finally(() => {
                        resumeAppQueriesAfterRecovery();
                        handleFocus(true);
                    });
                    return;
                }
                resumeAppQueriesAfterRecovery();
                handleFocus(true);
            };

            const onPageShow = (ev) => {
                if (ev.persisted) {
                    void runSessionPoolRecovery({ reason: 'bfcache', force: true });
                }
            };

            window.addEventListener('visibilitychange', onVisibility, false);
            window.addEventListener('pageshow', onPageShow, false);
            return () => {
                window.removeEventListener('visibilitychange', onVisibility);
                window.removeEventListener('pageshow', onPageShow);
            };
        });
    }, []);

    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    useEffect(() => {
        if (authRehydrated && wasAuthenticatedRef.current === true && !isAuthenticated) {
            resetAppQueryClientCaches();
        }
        if (authRehydrated) wasAuthenticatedRef.current = isAuthenticated;
    }, [authRehydrated, isAuthenticated]);

    if (!authRehydrated) {
        return <Cube3DLoader overlay size={48} />;
    }

    return (
        <ConfigProvider spin={{ indicator: <Cube3DLoader size={36} /> }} theme={finalAntdConfig}>
            <QueryClientProvider client={appQueryClient}>
                <ThemeProvider theme={currentTheme}>
                    <AppScrollbarsGlobalStyle />
                    <AntApp>
                        <MessageProvider>
                            <NotificationProvider>
                                <PreloadErrorOverlay />
                                <ViteHMRHandler />
                                <NotificationBannerHost />
                                <AbilityProvider>
                                    <Boundary>
                                        <AppDeployUpdateBar />
                                        <RoutesRoot />
                                    </Boundary>
                                </AbilityProvider>
                            </NotificationProvider>
                        </MessageProvider>
                    </AntApp>
                </ThemeProvider>
            </QueryClientProvider>
        </ConfigProvider>
    );
};

export default App;
